import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ExternalLink } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { BcpTierChip, BcpThemeTag } from './AccountsBcpsTab';
import { fmtMoney, fmtFyStart, fmtFyCode } from './accountsFormat';

/*
 * AccountsBcpDetail — expanded body for one BCP row: facts grid, what
 * the money funds, department implications, and the honest PANW
 * relevance assessment (grounded quotes included). Degrades plainly to
 * raw document content when the owner's enrichment run hasn't covered
 * this BCP. Data: GET /api/v1/bcps/:bcpId.
 */

const OPP_LABEL = {
  network_security: 'Network sec', sase_sse: 'SASE/SSE', zero_trust: 'Zero trust',
  cloud_security: 'Cloud sec', endpoint: 'Endpoint', secops: 'SecOps',
  data_security: 'Data sec', identity: 'Identity', ot_iot: 'OT/IoT',
};

function FiscalTable({ fiscal }) {
  if (!fiscal || fiscal.length === 0) return null;
  return (
    <div className="bcp-table-scroll">
      <table className="bcp-fiscal">
        <thead>
          <tr><th>Fund</th><th>FY</th><th className="r">Amount</th><th className="r">Positions</th></tr>
        </thead>
        <tbody>
          {fiscal.slice(0, 12).map((f, i) => (
            <tr key={i}>
              <td>{f.fundName === 'TOTAL_REQUEST' ? 'Total request' : `${f.fundCode || ''} ${f.fundName || ''}`.trim()}</td>
              <td>{fmtFyCode(f.fiscalYear)}</td>
              <td className="r">{f.amount ? fmtMoney(Number(f.amount)) : ''}</td>
              <td className="r">{f.positions ? Number(f.positions).toFixed(1) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AccountsBcpDetail({ bcp }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts-bcp-detail', bcp.bcpId],
    queryFn: () => apiClient.get(`/api/v1/bcps/${bcp.bcpId}`),
    staleTime: 60 * 60 * 1000,
  });

  if (isLoading) return <div className="bcp-detail"><p className="accounts-panel-note">Loading detail…</p></div>;
  if (error) {
    return (
      <div className="bcp-detail">
        <div className="accounts-error"><AlertCircle size={16} /> {error.message || "Couldn't load detail"}</div>
      </div>
    );
  }

  const a = data?.analysis || null;
  const fiscal = data?.fiscalDetails || [];
  const sections = data?.sections || [];
  const related = data?.relatedSubmissions || [];
  const totals = fiscal.filter((f) => f.fundName === 'TOTAL_REQUEST');

  const facts = [
    ['Cycle', `${fmtFyStart(bcp.fyStartYear)} · ${bcp.periodLabel || ''}`],
    ['Stage', bcp.itApprovalDocument || '—'],
    ['Outcome', bcp.outcomeStatus || 'proposed'],
    ['Positions', bcp.positions ? Number(bcp.positions).toFixed(0) : '—'],
    ['Project #', bcp.itProjectNumber || '—'],
    ['Total ask', bcp.requestAmount ? fmtMoney(Number(bcp.requestAmount)) : '—'],
  ];

  return (
    <div className="bcp-detail">
      <div className="bcp-facts">
        {facts.map(([label, value]) => (
          <div className="bcp-fact" key={label}>
            <div className="bcp-fact-label">{label}</div>
            <div className="bcp-fact-value">{value}</div>
          </div>
        ))}
      </div>
      {totals.length > 1 && (
        <p className="bcp-note">
          By year: {totals.map((f) => `${fmtFyCode(f.fiscalYear)} ${f.amount ? fmtMoney(Number(f.amount)) : '—'}`).join(' · ')}
        </p>
      )}

      {(bcp.primaryTheme || bcp.gist) && (
        <>
          <div className="ts-section-label bcp-sec-title">Business priority</div>
          <div className="bcp-rel-head">
            <BcpThemeTag theme={bcp.primaryTheme} />
            {(Array.isArray(bcp.secondaryThemes) ? bcp.secondaryThemes : []).map((t) => <BcpThemeTag theme={t} key={t} />)}
            {bcp.techAdjacent === true && <span className="bcp-theme-tag">tech-adjacent</span>}
          </div>
          {bcp.gist && <p className="bcp-summary">{bcp.gist}</p>}
        </>
      )}

      {a?.stale && (
        <p className="bcp-banner bcp-banner--stale">
          The underlying BCP content changed after this evaluation was generated — it will refresh on the next enrichment run.
        </p>
      )}
      {a?.narrativeCoverage === 'thin' && (
        <p className="bcp-banner">
          Narrative is thin for this BCP — the evaluation is grounded in limited text
          {a.analysisConfidence != null ? ` (confidence ${Math.round(a.analysisConfidence * 100)}%)` : ''}.
        </p>
      )}

      {!a && (
        <>
          {!bcp.primaryTheme && (
            <p className="bcp-banner">
              Not yet AI-evaluated — showing the raw document content. Evaluations are pre-generated in owner-run batches.
            </p>
          )}
          {(() => {
            const ps = sections.find((x) => x.sectionKey === 'problem_statement') || sections[0];
            return ps
              ? <p className="bcp-summary">{ps.content.slice(0, 600)}{ps.content.length > 600 ? '…' : ''}</p>
              : <p className="accounts-panel-note">No narrative sections were parsed for this document.</p>;
          })()}
          <FiscalTable fiscal={fiscal} />
        </>
      )}

      {a && (
        <>
          <div className="ts-section-label bcp-sec-title">What this funds</div>
          {a.purposeSummary && <p className="bcp-summary">{a.purposeSummary}</p>}

          {a.departmentImplications && (
            <>
              <div className="ts-section-label bcp-sec-title">Department implications</div>
              <p className="bcp-summary">{a.departmentImplications}</p>
            </>
          )}

          <div className="ts-section-label bcp-sec-title">Palo Alto relevance</div>
          <div className="bcp-rel-head">
            <BcpTierChip tier={a.panwTier} score={a.panwScore} />
            {a.panwScore != null && <span className="bcp-rel-score">{a.panwScore}/100</span>}
            {a.analysisConfidence != null && (
              <span className="bcp-note">confidence {Math.round(a.analysisConfidence * 100)}%</span>
            )}
            {(Array.isArray(a.opportunityAreas) ? a.opportunityAreas : []).slice(0, 6).map((area) => (
              <span className="bcp-theme-tag" key={area}>{OPP_LABEL[area] || area}</span>
            ))}
          </div>
          {a.panwRationale && <p className="bcp-summary">{a.panwRationale}</p>}
          {(Array.isArray(a.panwDrivingQuotes) ? a.panwDrivingQuotes : []).map((q, i) => (
            <blockquote className="bcp-quote" key={i}>
              “{q.quote}”<span className="bcp-quote-src"> — {q.section}</span>
            </blockquote>
          ))}
          {a.suggestedNextStep && (a.panwTier === 'high' || a.panwTier === 'medium') && (
            <p className="bcp-next">→ Next step: {a.suggestedNextStep}</p>
          )}

          <FiscalTable fiscal={fiscal} />
        </>
      )}

      <div className="bcp-detail-foot">
        {a && (
          <span className="bcp-note">
            {a.aiModel}{a.promptVersion ? ` · ${a.promptVersion}` : ''}{a.extractedAt ? ` · ${String(a.extractedAt).slice(0, 10)}` : ''}
          </span>
        )}
        {related.length > 0 && (
          <span className="bcp-note">also submitted: {related.map((rs) => rs.periodLabel).join(', ')}</span>
        )}
        {bcp.pdfUrl && (
          <a href={bcp.pdfUrl} target="_blank" rel="noreferrer" className="bcp-pdf-link">
            Open PDF <ExternalLink size={12} />
          </a>
        )}
      </div>
    </div>
  );
}
